/* The Stock Report page's API client and the shapes it returns.
 *
 * A month comes over as the registers' own rows — one per count per day,
 * one per waste type per day — with the two formula cells (produced,
 * closing) filled in. The page adds the month up itself, as the design's
 * script did on the same rows, so the two can be read line for line. */

import { request } from './api'

/** One count and quality on one day of the yarn register. Nulls are cells
 *  nobody filled in; `produced` is never null (a count with no autoconer
 *  output made 0). */
export interface YarnStockRow {
  date: string
  count_ne: number
  /** KW / SCH / CW — the sheet's "Yarn Type" column. */
  yarn_quality: string
  opening: number | null
  /** Autoconer output for the count that day. */
  produced: number
  dispatched: number | null
  issued: number | null
  adjustment: number | null
  /** The physical count where one was taken, else opening + produced −
   *  dispatched − issued + adjustment. */
  closing: number
  physical: number | null
  /** Physical less the movement figure; null until someone counts. */
  variance: number | null
  remark: string | null
}

/** One waste type on one day of the waste register. */
export interface WasteStockRow {
  date: string
  /** As the sheet spells it: HARDWASTE, MICRODUCT, LD … */
  waste_type: string
  department: string
  opening: number | null
  produced: number | null
  reused: number | null
  sold: number | null
  adjustment: number | null
  closing: number
  physical: number | null
  variance: number | null
  remark: string | null
}

export interface StockMonth {
  empty: false
  month: string
  months: { month: string; label: string }[]
  /** 'September 2026'. */
  label: string
  /** Every day either register has a row for, in order. */
  days: string[]
  yarn: YarnStockRow[]
  waste: WasteStockRow[]
}

export type StockSummary = StockMonth | { empty: true; months: []; month: null }

export interface StockPeriod {
  month: string
  label: string
  days: number
  yarn_rows: number
  waste_rows: number
  first_date: string
  last_date: string
  loads: {
    id: number
    filename: string | null
    uploaded_at: string | null
    uploaded_by: string | null
    days: number
    rows: number
    first_date: string | null
    last_date: string | null
  }[]
}

export const stock = {
  summary: (month?: string) =>
    request<StockSummary>('/stock/summary' + (month ? `?month=${month}` : '')),
  periods: () => request<{ periods: StockPeriod[] }>('/stock/periods'),
}
